/**
 * PresentationImagesSheet — sheet для прикрепления картинок к генерации
 * презентации. Открывается из пилюли «Картинки N/5» в footer'e виджета.
 *
 * Логика идентична бывшей inline-секции в PresentationControls:
 *   - До 5 картинок, PNG/JPG/WebP/GIF, до 5 МБ каждая
 *   - Загрузка через /files/upload (category='uploads')
 *   - URL + filename хранятся параллельными массивами в settings.presentation
 *
 * Открывается как nested sheet — variant='center' на десктопе, bottom на
 * мобилке. Применяется тот же компонент `Sheet` что SunoStyleSheet.
 */
import { useCallback, useRef, useState } from 'react'
import Sheet from '../../../components/Sheet'
import { useGenerate } from '../../../context/GenerateContext'
import { uploadFile } from '../../../api/images'
import type { PresentationSettings } from '../hooks/useGenerateSettings'

interface Props {
	open: boolean
	onClose: () => void
}

const MAX_IMAGES = 5
const MAX_IMAGE_BYTES = 5 * 1024 * 1024
const IMAGE_EXT_RE = /\.(png|jpe?g|webp|gif)$/i
const DEFAULT_PRES: PresentationSettings = { mode: 'edit' }

export default function PresentationImagesSheet({ open, onClose }: Props) {
	const { settings, userUpdateSetting: updateSetting, isLoading } = useGenerate()
	const pres = settings.presentation ?? DEFAULT_PRES

	const setPres = useCallback(
		(patch: Partial<PresentationSettings>) => {
			updateSetting('presentation', { ...pres, ...patch })
		},
		[pres, updateSetting],
	)

	const inputRef = useRef<HTMLInputElement>(null)
	const [uploading, setUploading] = useState(false)
	const [error, setError] = useState('')
	const [hover, setHover] = useState(false)

	const count = pres.imageUrls?.length ?? 0

	const handleFiles = async (files: File[]) => {
		const remaining = MAX_IMAGES - count
		if (files.length > remaining) {
			setError(`Можно прикрепить не более ${MAX_IMAGES} изображений`)
			return
		}
		for (const f of files) {
			if (!IMAGE_EXT_RE.test(f.name)) {
				setError('Поддерживаются PNG, JPG, WebP, GIF')
				return
			}
			if (f.size > MAX_IMAGE_BYTES) {
				setError(`Размер изображения — до 5 МБ (${f.name})`)
				return
			}
		}
		setError('')
		setUploading(true)
		try {
			const uploaded = await Promise.all(files.map((f) => uploadFile(f, 'uploads')))
			setPres({
				imageUrls: [...(pres.imageUrls ?? []), ...uploaded.map((u) => u.url)],
				imageFilenames: [...(pres.imageFilenames ?? []), ...files.map((f) => f.name)],
			})
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Ошибка загрузки')
		} finally {
			setUploading(false)
		}
	}

	const removeImage = (idx: number) => {
		const urls = pres.imageUrls ?? []
		const names = pres.imageFilenames ?? []
		setPres({
			imageUrls: urls.filter((_, i) => i !== idx),
			imageFilenames: names.filter((_, i) => i !== idx),
		})
	}

	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="center"
			title={`Картинки для вставки (${count}/${MAX_IMAGES})`}
			className="pres-images-sheet"
		>
			<div className="pres-images-sheet__body">
				{count < MAX_IMAGES && (
					<div
						className={
							'pres-controls__dropzone pres-controls__dropzone--compact' +
							(hover ? ' pres-controls__dropzone--hover' : '') +
							(isLoading || uploading ? ' pres-controls__dropzone--disabled' : '')
						}
						onClick={() => !uploading && !isLoading && inputRef.current?.click()}
						onDragOver={(e) => {
							e.preventDefault()
							if (!uploading && !isLoading) setHover(true)
						}}
						onDragLeave={() => setHover(false)}
						onDrop={(e) => {
							e.preventDefault()
							setHover(false)
							if (uploading || isLoading) return
							handleFiles(Array.from(e.dataTransfer.files))
						}}
						role="button"
						aria-disabled={uploading || isLoading}
						tabIndex={uploading || isLoading ? -1 : 0}
					>
						<div className="pres-controls__dropzone-title">
							{uploading ? 'Загружаем...' : '+ Добавить картинки'}
						</div>
						<div className="pres-controls__dropzone-hint">
							PNG, JPG, WebP, GIF · до 5 МБ каждая · осталось {MAX_IMAGES - count}
						</div>
						<input
							ref={inputRef}
							type="file"
							accept=".png,.jpg,.jpeg,.webp,.gif,image/*"
							multiple
							onChange={(e) => {
								handleFiles(Array.from(e.target.files ?? []))
								e.target.value = ''
							}}
							disabled={isLoading || uploading}
							style={{ display: 'none' }}
						/>
					</div>
				)}

				{count > 0 && (
					<div className="pres-controls__images">
						{(pres.imageUrls ?? []).map((url, idx) => (
							<div key={url} className="pres-controls__chip pres-controls__chip--compact">
								<span className="pres-controls__file-index" aria-hidden="true">#{idx}</span>
								<span className="pres-controls__chip-meta">
									<span className="pres-controls__chip-title">
										{pres.imageFilenames?.[idx] ?? `image-${idx}`}
									</span>
								</span>
								<button
									type="button"
									className="pres-controls__chip-remove"
									onClick={() => removeImage(idx)}
									disabled={isLoading}
									aria-label="Убрать картинку"
								>
									×
								</button>
							</div>
						))}
					</div>
				)}

				<p className="pres-controls__hint">
					AI сам решит куда вставить. Подсказать в промпте: «вставь #0 на слайд 2».
				</p>

				{error && <div className="pres-controls__error">{error}</div>}
			</div>

			{/* Footer — кнопка «Готово» закрывает sheet. Настройки сохраняются
			    автоматически при каждой загрузке/удалении, кнопка — только визуальный
			    commit чтобы юзер чувствовал что действие завершено. */}
			<div className="pres-images-sheet__actions">
				<button
					type="button"
					className="suno-sheet-btn suno-sheet-btn--primary"
					onClick={onClose}
					disabled={uploading}
				>
					Готово {count > 0 ? `· ${count} из ${MAX_IMAGES}` : ''}
				</button>
			</div>
		</Sheet>
	)
}
